import { useState, useEffect, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { 
  Plane, CircleDot, Dices, Cherry, Newspaper, Sparkles, 
  TrendingUp, Zap, Trophy, Users, Volume2, Citrus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

// Game data with visual thumbnails
const games = [
  { 
    path: "/games/aviator", 
    title: "Aviator", 
    icon: Plane, 
    color: "#FF3B30",
    gradient: "from-red-600/40 to-orange-600/20",
    image: "https://images.unsplash.com/photo-1436891620584-47fd0e565afb?w=400&h=300&fit=crop",
    players: 1247,
    hot: true,
    multiplier: "100x"
  },
  { 
    path: "/games/slots", 
    title: "Slots", 
    icon: Cherry, 
    color: "#FF007A",
    gradient: "from-pink-600/40 to-purple-600/20",
    image: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=400&h=300&fit=crop",
    players: 2341,
    hot: true,
    multiplier: "50x"
  },
  { 
    path: "/games/dice", 
    title: "Dice", 
    icon: Dices, 
    color: "#007AFF",
    gradient: "from-blue-600/40 to-cyan-600/20",
    image: "https://images.unsplash.com/photo-1522069213448-443a614da9b6?w=400&h=300&fit=crop",
    players: 892,
    multiplier: "98x"
  },
  { 
    path: "/games/coinbalance", 
    title: "Coin Balance", 
    icon: CircleDot, 
    color: "#FFD600",
    gradient: "from-yellow-600/40 to-amber-600/20",
    image: "https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=400&h=300&fit=crop",
    players: 523,
    multiplier: "10x"
  },
  { 
    path: "/games/mines", 
    title: "Mines", 
    icon: Zap, 
    color: "#FF6B00",
    gradient: "from-orange-600/40 to-red-600/20",
    image: "https://images.unsplash.com/photo-1518544866330-4e716499f800?w=400&h=300&fit=crop",
    players: 756,
    hot: true,
    multiplier: "1000x"
  },
  { 
    path: "/games/plinko", 
    title: "Plinko", 
    icon: TrendingUp, 
    color: "#00F0FF",
    gradient: "from-cyan-600/40 to-blue-600/20",
    image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=300&fit=crop",
    players: 445,
    multiplier: "10x"
  },
  { 
    path: "/games/wheel", 
    title: "Wheel", 
    icon: Trophy, 
    color: "#FFD600",
    gradient: "from-yellow-600/40 to-orange-600/20",
    image: "https://images.unsplash.com/photo-1606167668584-78701c57f13d?w=400&h=300&fit=crop",
    players: 678,
    multiplier: "10x"
  },
  { 
    path: "/games/coinlemon", 
    title: "Coin on Lemon", 
    icon: Citrus, 
    color: "#FFD600",
    gradient: "from-yellow-500/40 to-green-600/20",
    image: "https://images.unsplash.com/photo-1587486913049-53fc88980cfc?w=400&h=300&fit=crop",
    players: 312,
    hot: true,
    multiplier: "25x"
  },
];

// Live wins simulator
const generateWin = () => {
  const names = ["Player***", "Lucky7***", "Win***", "Bet***", "Gold***", "Star***", "King***"];
  const gameTypes = ["Aviator", "Slots", "Dice", "Coin Balance"];
  const amounts = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000];
  return {
    id: Date.now() + Math.random(),
    name: names[Math.floor(Math.random() * names.length)] + Math.floor(Math.random() * 100),
    game: gameTypes[Math.floor(Math.random() * gameTypes.length)],
    amount: amounts[Math.floor(Math.random() * amounts.length)],
    multiplier: (1 + Math.random() * 50).toFixed(2)
  };
};

export const DashboardPage = () => {
  const { user } = useContext(UserContext);
  const [liveWins, setLiveWins] = useState([]);
  const [jackpot, setJackpot] = useState(2547893);

  useEffect(() => {
    // Initialize with some wins
    const initial = Array.from({ length: 8 }, generateWin);
    setLiveWins(initial);

    // Add new wins periodically
    const interval = setInterval(() => {
      setLiveWins(prev => [generateWin(), ...prev.slice(0, 7)]);
      setJackpot(prev => prev + Math.floor(Math.random() * 1000));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Layout>
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Hero Banner - Featured Game */}
          <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-6 group cursor-pointer">
            <img 
              src="https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1200&h=400&fit=crop"
              alt="Casino"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            
            {/* Jackpot Counter */}
            <div className="absolute top-4 right-4 px-4 py-2 rounded-lg bg-black/60 backdrop-blur-sm border border-[#FFD600]/30">
              <div className="text-[#FFD600] text-xs font-medium mb-1">JACKPOT</div>
              <div className="text-2xl font-bold text-white font-mono">
                ₹{jackpot.toLocaleString()}
              </div>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 rounded bg-[#FF3B30] text-white text-xs font-bold animate-pulse">LIVE</span>
                <span className="text-[#00E701] text-sm font-medium flex items-center gap-1">
                  <Users className="w-3 h-3" /> 1,247 playing
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white font-['Rajdhani'] mb-2">
                AVIATOR
              </h1>
              <p className="text-white/70 text-sm md:text-base mb-4 max-w-md">
                Cash out before the plane flies away! Up to 100x multiplier
              </p>
              <Link to="/games/aviator">
                <Button className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold px-8 py-6 text-lg shadow-[0_0_30px_rgba(0,231,1,0.5)] hover:shadow-[0_0_50px_rgba(0,231,1,0.7)] transition-shadow">
                  <Zap className="w-5 h-5 mr-2" /> PLAY NOW
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#00E701]/20 to-transparent border border-[#00E701]/20">
              <div className="text-[#00E701] text-2xl font-bold font-mono">
                ₹{(user?.balance || 0).toLocaleString()}
              </div>
              <div className="text-white/50 text-xs">Your Balance</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#FFD600]/20 to-transparent border border-[#FFD600]/20">
              <div className="text-[#FFD600] text-2xl font-bold font-mono">24/7</div>
              <div className="text-white/50 text-xs">Instant Payouts</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/20">
              <div className="text-[#007AFF] text-2xl font-bold font-mono">5,234</div>
              <div className="text-white/50 text-xs">Online Players</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#FF007A]/20 to-transparent border border-[#FF007A]/20">
              <div className="text-[#FF007A] text-2xl font-bold font-mono">₹10Cr+</div>
              <div className="text-white/50 text-xs">Total Paid Out</div>
            </div>
          </div>

          {/* Games Grid - Visual Cards */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white font-['Rajdhani'] flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FFD600]" /> CASINO GAMES
              </h2>
              <Link to="/games/slots" className="text-[#00E701] text-sm hover:underline">
                View All
              </Link>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {games.map((game) => {
                const Icon = game.icon;
                return (
                  <Link
                    key={game.path}
                    to={game.path}
                    className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-300"
                    data-testid={`game-card-${game.title.toLowerCase().replace(' ', '-')}`}
                  >
                    {/* Background Image */}
                    <img 
                      src={game.image}
                      alt={game.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    
                    {/* Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${game.gradient} opacity-80`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    
                    {/* Hot Badge */}
                    {game.hot && (
                      <div className="absolute top-2 left-2 px-2 py-1 rounded bg-[#FF3B30] text-white text-[10px] font-bold flex items-center gap-1">
                        <Zap className="w-3 h-3" /> HOT
                      </div>
                    )}
                    
                    {/* Players Count */}
                    <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-white/80 text-[10px] flex items-center gap-1">
                      <Users className="w-3 h-3" /> {game.players}
                    </div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-5 h-5" style={{ color: game.color }} />
                        <span className="text-white font-bold font-['Rajdhani'] text-lg">{game.title}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-xs">Max Win</span>
                        <span className="text-[#00E701] font-mono font-bold text-sm">{game.multiplier}</span>
                      </div>
                    </div>
                    
                    {/* Hover Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                      <div className="px-6 py-2 rounded-full bg-[#00E701] text-black font-bold text-sm shadow-[0_0_20px_rgba(0,231,1,0.6)]">
                        PLAY
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* News Betting & AI Creator Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Predictions Section */}
            <Link 
              to="/news"
              className="group relative h-48 rounded-xl overflow-hidden border border-white/10 hover:border-[#00E701]/50 transition-all"
            >
              <img 
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=300&fit=crop"
                alt="News Betting"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/50" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                <div className="flex items-center gap-2 mb-2">
                  <Newspaper className="w-5 h-5 text-[#00E701]" />
                  <span className="text-[#00E701] text-xs font-bold">PREDICTION MARKETS</span>
                </div>
                <h3 className="text-xl font-bold text-white font-['Rajdhani'] mb-1">News Betting</h3>
                <p className="text-white/60 text-sm mb-3">Bet on real-world events and trending news</p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 rounded bg-[#00E701]/20 text-[#00E701] text-xs">Crypto</span>
                  <span className="px-2 py-1 rounded bg-[#007AFF]/20 text-[#007AFF] text-xs">Sports</span>
                  <span className="px-2 py-1 rounded bg-[#FF007A]/20 text-[#FF007A] text-xs">Tech</span>
                </div>
              </div>
            </Link>
            
            {/* AI Creator */}
            <Link 
              to="/ai-creator"
              className="group relative h-48 rounded-xl overflow-hidden border border-white/10 hover:border-[#00F0FF]/50 transition-all"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/20 via-[#007AFF]/10 to-[#FF007A]/20" />
              <div className="absolute inset-0 bg-[#0F0F10]/80" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-[#00F0FF] text-xs font-bold">AI POWERED</span>
                </div>
                <h3 className="text-xl font-bold text-white font-['Rajdhani'] mb-1">Create Your Game</h3>
                <p className="text-white/60 text-sm mb-3">Use AI to create custom betting games</p>
                <div className="h-8 flex items-center">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#007AFF] border-2 border-[#0F0F10]" />
                    ))}
                  </div>
                  <span className="ml-3 text-white/50 text-xs">+127 games created</span>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Live Wins Sidebar - Desktop Only */}
        <div className="hidden xl:block w-72 shrink-0">
          <div className="sticky top-20 rounded-xl bg-[#1A1B1F] border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#00E701] animate-pulse" />
                <span className="text-white font-bold text-sm">LIVE WINS</span>
              </div>
              <Volume2 className="w-4 h-4 text-white/40" />
            </div>
            
            {/* Wins List */}
            <div className="max-h-[500px] overflow-y-auto">
              {liveWins.map((win, idx) => (
                <div 
                  key={win.id}
                  className={`p-3 border-b border-white/5 flex items-center gap-3 ${
                    idx === 0 ? 'bg-[#00E701]/10 animate-pulse' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center text-black text-xs font-bold">
                    {win.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{win.name}</div>
                    <div className="text-white/50 text-xs">{win.game}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#00E701] font-mono font-bold text-sm">
                      ₹{win.amount.toLocaleString()}
                    </div>
                    <div className="text-white/40 text-xs">{win.multiplier}x</div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Footer Stats */}
            <div className="p-4 bg-black/20 grid grid-cols-2 gap-2">
              <div className="text-center">
                <div className="text-[#FFD600] font-mono font-bold">₹2.5Cr</div>
                <div className="text-white/40 text-[10px]">Won Today</div>
              </div>
              <div className="text-center">
                <div className="text-[#00E701] font-mono font-bold">15,234</div>
                <div className="text-white/40 text-[10px]">Bets Today</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
